import * as yup from '@if/core/yup'
import { type FormSetup, useConfirmDialog, useConfirmDiscardDialog } from '@if/mui'
import { Alert, Divider, Stack } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useCreateMerchantPaymentPlans, useUpdateMerchant } from '@src/data/api/merchants-api/merchants-api'
import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import { EBannerOwnedFieldKey, EPromotionMode } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { isEqual } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { UseFormReturn, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { buildUpdateMerchantDto } from '../buildUpdateMerchantDto'
import CustomPromotionPanel from '../CustomPromotionPanel'
import { MerchantPlanPromoRulesSchema } from '../MerchantPromoPlanPanel'
import MerchantPromotionPanel from '../MerchantPromotionPanel'
import PromotionModeSelector from '../PromotionModeSelector'
import { MerchantTabConfig } from './MerchantGeneralTab'

export const MerchantPromotionFormSchema = MerchantPlanPromoRulesSchema.concat(
  yup.object().shape({
    paymentPlansToCreate: yup.array().of(yup.mixed<ApplicableMerchantPaymentPlan>().required()).required().default([]),
    promotionMode: yup.mixed<EPromotionMode>().required().default(EPromotionMode.Preconfigured),
    // Custom promotion notification recipients: committing to custom mode requires
    // at least one address on file before the tab can be saved.
    promoNotificationEmailList: yup
      .array()
      .of(yup.string().email().required())
      .required()
      .when('promotionMode', {
        is: EPromotionMode.Custom,
        then: (schema) => schema.min(1),
      })
      .default([]),
    // Custom Buydown rate rebate (single source of truth on the merchant). 0 = no rebate, max 5%.
    flashPlanRateRebate: yup.number().min(0).max(0.05).required().default(0),
  }),
)

export type MerchantPromotionFormValues = yup.InferType<typeof MerchantPromotionFormSchema>

interface MerchantPromotionTabContentProps {
  merchant: Merchant
  merchantPaymentPlans: MerchantPaymentPlan[]
  isPaymentPlanFetching: boolean
}

const MerchantPromotionTabContent = ({
  merchant,
  merchantPaymentPlans,
  isPaymentPlanFetching,
}: MerchantPromotionTabContentProps) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const { control, getValues, setValue, resetField } = useFormContext<MerchantPromotionFormValues>()
  const paymentPlansToCreate = useWatch({ control, name: 'paymentPlansToCreate' })
  const promotionMode = useWatch({ control, name: 'promotionMode' })

  const confirmModeSwitch = useConfirmDiscardDialog({
    title: t('merchant.promotionMode.switchConfirmTitle'),
    message: (
      <Stack gap={2}>
        <span>{t('merchant.promotionMode.switchConfirmMessage')}</span>
        <Divider />
        <span>{t('merchant.promotionMode.customWarningMessage')}</span>
      </Stack>
    ),
  })

  const confirmCustomMode = useConfirmDialog({
    title: t('merchant.promotionMode.customWarningTitle'),
    message: t('merchant.promotionMode.customWarningMessage'),
  })

  const confirmLeaveCustom = useConfirmDiscardDialog({
    title: t('merchant.promotionMode.switchConfirmTitle'),
    message: t('merchant.promotionMode.switchConfirmMessage'),
  })

  const handleModeChange = (previousMode: EPromotionMode) => {
    if (previousMode === EPromotionMode.Custom) {
      const hasUnsavedChanges = !isEqual(
        getValues('promoNotificationEmailList'),
        merchant.promoNotificationEmailList ?? [],
      )
      if (!hasUnsavedChanges) {
        return
      }

      void confirmLeaveCustom.open().then((discard) => {
        if (discard) {
          resetField('promoNotificationEmailList')
        } else {
          setValue('promotionMode', previousMode, { shouldDirty: true })
        }
      })
      return
    }

    // Flipping to Custom invalidates the Flash/Elite/Flex plans, so it is always gated.
    // Only the preconfigured side holds these unsaved edits in the form (same change
    // detection as onSubmit); they add the discard notice to the warning.
    const hasUnsavedChanges =
      getValues('paymentPlansToCreate').length > 0 || !isEqual(getValues('planPromoRules'), merchant.planPromoRules)

    const confirmation = hasUnsavedChanges ? confirmModeSwitch : confirmCustomMode
    void confirmation.open().then((confirmed) => {
      if (confirmed) {
        // Leaving a mode abandons its in-form edits: drop them back to their defaults.
        resetField('paymentPlansToCreate')
        resetField('planPromoRules')
      } else {
        setValue('promotionMode', previousMode, { shouldDirty: true })
      }
    })
  }

  const applicableMerchantPaymentPlans: ApplicableMerchantPaymentPlan[] = useMemo(
    () => [...paymentPlansToCreate, ...merchantPaymentPlans],
    [paymentPlansToCreate, merchantPaymentPlans],
  )

  // When the parent banner owns the buydown, the merchant can only view the inherited plan:
  // the mode selector and buydown editor are locked.
  const isBuydownBannerOwned = (merchant.bannerOwnedFields ?? []).includes(EBannerOwnedFieldKey.CustomBuydown)

  return (
    <Stack gap={3}>
      {featureFlags?.useCustomFlashPlan && (
        <PromotionModeSelector
          control={control}
          name="promotionMode"
          onChange={isBuydownBannerOwned ? undefined : () => handleModeChange(promotionMode)}
          disabled={isBuydownBannerOwned}
        />
      )}
      {isBuydownBannerOwned && <Alert severity="info">{t('merchant.customBuydown.managedByBanner')}</Alert>}
      {featureFlags?.useCustomFlashPlan && promotionMode === EPromotionMode.Custom ? (
        <CustomPromotionPanel id={merchant.id} readOnly={isBuydownBannerOwned} />
      ) : (
        <MerchantPromotionPanel
          merchant={merchant}
          isPaymentPlanFetching={isPaymentPlanFetching}
          merchantPaymentPlans={merchantPaymentPlans}
          merchantPaymentPlansToCreate={paymentPlansToCreate}
          applicableMerchantPaymentPlans={applicableMerchantPaymentPlans}
          planPromoRules={getValues('planPromoRules')}
          onMerchantPaymentPlanAdded={(dto) => {
            setValue('paymentPlansToCreate', [...getValues('paymentPlansToCreate'), dto], { shouldDirty: true })
          }}
        />
      )}
    </Stack>
  )
}

/**
 * Builds the Promotion tab's form config and body. Saves payment plans (separate
 * endpoint) and/or the promo-rules slice, depending on what changed.
 */
export function useMerchantPromotionTab(
  merchant: Merchant,
  merchantPaymentPlans: MerchantPaymentPlan[],
  isPaymentPlanFetching: boolean,
): MerchantTabConfig<MerchantPromotionFormValues> {
  const [updateMerchant] = useUpdateMerchant()
  const [createMerchantPaymentPlans] = useCreateMerchantPaymentPlans()

  const useFormSetup = useCallback(
    (): FormSetup<MerchantPromotionFormValues> => ({
      schema: MerchantPromotionFormSchema,
      mode: 'onBlur',
      defaultValues: {
        planPromoRules: merchant.planPromoRules,
        paymentPlansToCreate: [],
        promotionMode: merchant.flashPlanDefinedInManageMerchant ? EPromotionMode.Custom : EPromotionMode.Preconfigured,
        promoNotificationEmailList: merchant.promoNotificationEmailList ?? [],
        flashPlanRateRebate: merchant.flashPlanRateRebate ?? 0,
      },
    }),
    [
      merchant.planPromoRules,
      merchant.flashPlanDefinedInManageMerchant,
      merchant.promoNotificationEmailList,
      merchant.flashPlanRateRebate,
    ],
  )

  const onSubmit = useCallback(
    async (values: MerchantPromotionFormValues, methods: UseFormReturn<MerchantPromotionFormValues>) => {
      const promises: Promise<unknown>[] = []

      if (values.paymentPlansToCreate.length > 0) {
        promises.push(createMerchantPaymentPlans({ merchantId: merchant.id, dtos: values.paymentPlansToCreate }))
      }

      // The selected promotion mode is persisted as FlashPlanDefinedInManageMerchant:
      // Custom = true (flash plans defined here, shielded from the Alis sync), Preconfigured = false.
      const flashPlanDefinedInManageMerchant = values.promotionMode === EPromotionMode.Custom
      const modeChanged = flashPlanDefinedInManageMerchant !== merchant.flashPlanDefinedInManageMerchant
      if (
        modeChanged ||
        !isEqual(values.planPromoRules, merchant.planPromoRules) ||
        !isEqual(values.promoNotificationEmailList, merchant.promoNotificationEmailList ?? []) ||
        values.flashPlanRateRebate !== (merchant.flashPlanRateRebate ?? 0)
      ) {
        promises.push(
          updateMerchant(
            buildUpdateMerchantDto(merchant, {
              planPromoRules: values.planPromoRules,
              flashPlanDefinedInManageMerchant,
              promoNotificationEmailList: values.promoNotificationEmailList,
              flashPlanRateRebate: values.flashPlanRateRebate,
            }),
          ),
        )
      }

      await Promise.all(promises)
      methods.reset({ ...values, paymentPlansToCreate: [] })
    },
    [merchant, updateMerchant, createMerchantPaymentPlans],
  )

  const children = useCallback(
    () => (
      <MerchantPromotionTabContent
        merchant={merchant}
        merchantPaymentPlans={merchantPaymentPlans}
        isPaymentPlanFetching={isPaymentPlanFetching}
      />
    ),
    [merchant, merchantPaymentPlans, isPaymentPlanFetching],
  )

  return { useFormSetup, onSubmit, children }
}
